"use client";

import { getPaginationArray, getParamFromUrlString } from "@/lib/utilities";
import { PaginationBar } from "@repo/ui/components/pagination-bar";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { type HTMLAttributes, useEffect, useState } from "react";

type PerPageAndTotalItemsProps = {
  perPage: number;
  totalItems: number;
  totalPages?: never;
};

type TotalPagesProps = {
  perPage?: never;
  totalItems?: never;
  totalPages: number | string;
};

type PaginationWrapperProps = HTMLAttributes<HTMLDivElement> &
  (PerPageAndTotalItemsProps | TotalPagesProps) & {
    setPage?: (page: number) => void;
  };

const parseTotalPages = (totalPages: string | number | undefined) => {
  if (typeof totalPages === "string") {
    return Number(getParamFromUrlString(totalPages, "page"));
  } else {
    return totalPages;
  }
};

export const PaginationWrapper = ({
  perPage = 15,
  totalItems = 1,
  totalPages,
  setPage,
  ...props
}: PaginationWrapperProps) => {
  const [delta, setDelta] = useState(2);
  const searchParams = useSearchParams();
  const { replace } = useRouter();
  const pathname = usePathname();
  const params = new URLSearchParams(searchParams);
  const innerTotalPages =
    parseTotalPages(totalPages) ?? Math.ceil(totalItems / perPage);
  const current = params.get("page")
    ? parseInt(params.get("page") as string)
    : 1;
  const pages = getPaginationArray(current, innerTotalPages, delta);

  const handleChangePage = (page: number) => {
    if (setPage) {
      setPage(page);
    } else {
      params.set("page", page.toString());
      replace(`${pathname}?${params.toString()}`);
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      setDelta(width < 640 ? 0 : 2);
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getPageUrl = (page: number) => {
    const localParams = new URLSearchParams(searchParams);
    localParams.set("page", page.toString());
    return `${pathname}?${localParams.toString()}`;
  };

  return (
    <PaginationBar
      className={props.className}
      current={current}
      getPageUrl={getPageUrl}
      onPageChange={handleChangePage}
      pages={pages}
      totalPages={innerTotalPages}
    />
  );
};
